<template>
	<validation-observer
		v-if="!showCodeChangeConfirm"
		#default="{ handleSubmit }"
	>
		<Modal
			width="600px"
		>
			<template #modal-title>
				{{ isEdit ? 'Edit' : 'Add' }} Language
				<v-spacer />
				<IconButton @click="$emit('close')">
					mdi-close
				</IconButton>
			</template>
			<template #modal-content>
				<div class="cassie-vertical-md">
					<Dropdown
						v-if="!isEdit"
						v-model="optionTranslation.languageLookupId"
						:disabled="userFullPermissions"
						:items="languages"
						searchable
						label="Language *"
						rules="required"
						item-value="languageLookupId"
						item-text="languageName"
						@change="onLanguageLookupChange"
					/>
					<TextField
						v-model="optionTranslation.languageCode"
						:disabled="userFullPermissions"
						rules="required"
						:placeholder="calculateSuggestions"
						label="Language Code"
					/>
					<TextField
						v-model="optionTranslation.languageName"
						:disabled="userFullPermissions"
						label="Language Name"
						rules="required"
					/>
					<TextField
						v-model="optionTranslation.localLanguageName"
						:disabled="userFullPermissions"
						label="Local Language Name"
						rules="required"
					/>
					<v-radio-group
						v-model="optionTranslation.enabledInPublicPortal"
						:disabled="userFullPermissions"
						hide-details
						class="mt-0"
					>
						<template #label>
							Is enabled in the Public Portal?
						</template>
						<div class="d-flex">
							<v-radio
								:disabled="userFullPermissions"
								label="Yes"
								:value="true"
								class="my-0 mr-4"
							/>
							<v-radio
								:disabled="userFullPermissions"
								label="No"
								:value="false"
								class="my-0"
							/>
						</div>
					</v-radio-group>
				</div>
			</template>
			<template #modal-footer>
				<SecondaryActionButton @click="$emit('close')">
					Cancel
				</SecondaryActionButton>
				<PrimaryActionButton
					v-if="!userFullPermissions"
					:disabled="isSubmitting"
					@click="handleSubmit(submit)"
				>
					{{ isEdit ? 'Save' : 'Create' }}
				</PrimaryActionButton>
			</template>
		</Modal>
	</validation-observer>
	<Modal
		v-else
		width="600px"
	>
		<template #modal-title>
			Update Language Code
		</template>
		<template #modal-content>
			<div class="cassie-vertical-md">
				<p>
					This language code may be referenced elsewhere in your Syrenis implementation. Updating it here will not update those references. They will continue to point to the previous code, which will no longer exist, and will need to be updated manually.
				</p>
				<p>
					Creating a new language code leaves the original, and everything using it, untouched.
				</p>
			</div>
		</template>
		<template #modal-footer>
			<SecondaryActionButton
				:disabled="isSubmitting"
				data-testid="language-code-change-cancel"
				@click="showCodeChangeConfirm = false"
			>
				Cancel
			</SecondaryActionButton>
			<SecondaryActionButton
				:disabled="isSubmitting"
				data-testid="language-code-change-create-new"
				@click="createNew"
			>
				Create New
			</SecondaryActionButton>
			<PrimaryActionButton
				:disabled="isSubmitting"
				data-testid="language-code-change-update-existing"
				@click="updateExisting"
			>
				Update Existing
			</PrimaryActionButton>
		</template>
	</Modal>
</template>
<script>
import Modal from '../../../../../../shared/components/modal.vue'
import { createLanguage, updateLanguage } from '../../../../../../shared/utils/api/languages.js'
import { resolveLanguageLookupId } from './resolve-language-lookup-id.js'
import { showSnackbar } from '../../../../../../shared/state/snackbar.js'
import TextField from '../../../../../../shared/components/text-field.vue'
import SecondaryActionButton from '../../../../../../shared/components/secondary-action-button.vue'
import PrimaryActionButton from '../../../../../../shared/components/primary-action-button.vue'
import Dropdown from '../../../../../../shared/components/dropdown.vue'
import IconButton from '../../../../../../shared/components/icon-button.vue'
export default {
	components: { PrimaryActionButton, SecondaryActionButton, TextField, Modal, Dropdown, IconButton },
	props: {
		optionTranslationToEdit: Object,
		userFullPermissions: {
			type: Boolean,
			default: false
		},
		languages: {
			type: Array,
			default: () => []
		},
		isEdit: {
			type: Boolean,
			default: false
		}
	},
	data () {
		return {
			optionTranslation: {
				...JSON.parse(JSON.stringify(this.optionTranslationToEdit)),
				localLanguageName: this.optionTranslationToEdit?.localLanguageName || ''
			},
			showAddModal: false,
			showCodeChangeConfirm: false,
			isSubmitting: false
		}
	},
	computed: {
		calculateSuggestions () {
			return this.languages.find(({ languageLookupId }) => languageLookupId === this.optionTranslation.languageLookupId)
				?.googleLanguageCode ?? ''
		},
		languageCodeChanged () {
			const original = this.optionTranslationToEdit?.languageCode ?? ''
			const current = this.optionTranslation.languageCode ?? ''
			return original.toLowerCase() !== current.toLowerCase()
		}
	},
	methods: {
		onLanguageLookupChange (id) {
			const selected = this.languages.find(l => l.languageLookupId === id)
			if (selected) {
				this.optionTranslation.languageCode = selected.googleLanguageCode
				this.optionTranslation.languageName = selected.languageName
				this.optionTranslation.localLanguageName = selected.localLanguageName
			}
		},
		async submit () {
			const { languageCode, languageID } = this.optionTranslation
			// Case-insensitive duplicate check
			if (this.languages.some(lang => lang.languageCode?.toLowerCase() === languageCode?.toLowerCase() && lang.languageID !== languageID)) {
				showSnackbar({ text: `Language code '${languageCode}' already exists.`, color: 'error' })
				return
			}
			// The language code is a mutable natural key with no cascade, so
			// changing it on an existing language strands every banner and text
			// template still referencing the old code. Make the operator decide
			// whether to update it in place or create a new language instead.
			if (this.isEdit && this.languageCodeChanged) {
				this.showCodeChangeConfirm = true
				return
			}
			await this.runSave(this.isEdit ? this.persistUpdate : this.persistCreate)
		},
		async updateExisting () {
			await this.runSave(this.persistUpdate)
		},
		async createNew () {
			await this.runSave(this.persistCreate)
		},
		async runSave (persist) {
			// Guard against a double click firing the create/update call twice
			// before the modal closes.
			if (this.isSubmitting) return
			this.isSubmitting = true
			try {
				// A persist step returns false to signal it bailed (e.g. a
				// validation guard), so the modal is not closed as if it saved.
				const succeeded = await persist()
				if (succeeded !== false) this.finish()
			} finally {
				this.isSubmitting = false
			}
		},
		async persistUpdate () {
			const { enabledInPublicPortal, languageCode, languageID, languageName, localLanguageName } = this.optionTranslation
			await updateLanguage(
				languageID,
				languageName.replace(' (Default)', ''),
				languageCode,
				enabledInPublicPortal,
				false, // default auto translation to false
				localLanguageName
			)
			if (languageID === 0) {
				// Reload Default Language
				this.defaultLanguage = languageName
			}
			showSnackbar(`${languageName} has been updated`)
		},
		async persistCreate () {
			const { enabledInPublicPortal, languageCode, languageName, localLanguageName } = this.optionTranslation
			const languageLookupId = resolveLanguageLookupId(this.optionTranslation, this.optionTranslationToEdit, this.languages)
			if (!languageLookupId) {
				// No base language could be matched (e.g. a default whose code is
				// not in the lookup list). Fail with a clear message rather than
				// sending a request the API will reject.
				showSnackbar({ text: 'Could not determine a base language for this code, so the language was not created. Add it from the language list instead.', color: 'error' })
				return false
			}
			await createLanguage(
				languageLookupId,
				languageName,
				languageCode,
				enabledInPublicPortal,
				false, // default auto translation to false
				localLanguageName
			)
			showSnackbar(`${languageName} has been created`)
		},
		finish () {
			this.$emit('saved')
			this.$emit('close')
		}
	}
}
</script>
